import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import P2pLayout from "./P2pLayout";
import "./p2p.css";

const CustomSelect = ({ options, value, onChange, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`p2p-custom-select-container ${className || ""}`} ref={dropdownRef}>
      <div className="p2p-custom-select-header" onClick={() => setIsOpen(!isOpen)}>
        <span>{options.find(o => o.value === value)?.label || value}</span>
        <i className={`ri-arrow-down-s-line ${isOpen ? 'open' : ''}`}></i>
      </div>
      {isOpen && (
        <ul className="p2p-custom-select-list">
          {options.map(opt => (
            <li
              key={opt.value}
              className={`p2p-custom-select-item ${value === opt.value ? 'selected' : ''}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const P2pDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("buy");
  const [activeToken, setActiveToken] = useState("USDT");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [payment, setPayment] = useState("All payments");
  const [region, setRegion] = useState("All Regions");
  const [filterText, setFilterText] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const side = params.get('side') || location.state?.side;
    const asset = params.get('asset') || location.state?.asset;
    const curr = params.get('currency') || location.state?.currency;
    const amt = params.get('amount') || location.state?.amount;
    const pay = params.get('payment') || location.state?.payment;

    if (side) setActiveTab(side.toLowerCase());
    if (asset) setActiveToken(asset.toUpperCase());
    if (curr) setCurrency(curr.toUpperCase());
    if (amt) setAmount(amt);
    if (pay) setPayment(pay);
  }, [location.search, location.state]);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [receiveAmount, setReceiveAmount] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [modalPaymentMethod, setModalPaymentMethod] = useState("Other");
  const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false);
  const [isRequirementModalOpen, setIsRequirementModalOpen] = useState(false);
  const [selectedTutorial, setSelectedTutorial] = useState(null);

  const tutorialCards = [
    {
      id: "buy",
      title: "How to Buy Crypto with P2P",
      subtitle: "Step-by-step guide to buying crypto via P2P on AGCX platform safely.",
      image: "/images/p2p/p2p_trading_img.png",
      tag: "Buyer Guide",
      intro: "Follow this easy step-by-step walkthrough to purchase USDT, BTC, or other cryptocurrencies directly from verified sellers using local payment methods like UPI, IMPS, and Bank Transfer with 0% trading fees.",
      steps: [
        {
          num: "01",
          heading: "Select 'BUY' & Choose Your Currency",
          detail: "Open the P2P Dashboard and ensure the 'BUY' tab is selected. Pick your crypto (e.g. USDT) and fiat currency (e.g. INR, USD, AED). You can filter offers by payment method, price, or order limits."
        },
        {
          num: "02",
          heading: "Place an Order with a Verified Seller",
          detail: "Check the seller's completion rate and positive feedback score. Click 'BUY', enter the amount you want to pay or receive, review the seller's remarks, and confirm your order."
        },
        {
          num: "03",
          heading: "Transfer Funds via Bank/UPI",
          detail: "Copy the seller's payment details provided on the order page. Open your bank or UPI app and transfer the exact amount within the countdown timer. Never mention crypto terms in the transaction remarks."
        },
        {
          num: "04",
          heading: "Click 'Transferred, Notify Seller'",
          detail: "Once the payment is successfully completed, return to AGCX and click 'Transferred, Notify Seller'. Do NOT click this before actually transferring the funds."
        },
        {
          num: "05",
          heading: "Receive Crypto in Your Wallet",
          detail: "The seller's crypto is held safely in AGCX Escrow. As soon as the seller verifies receipt of the money in their bank, the crypto will be automatically released to your AGCX Funding Wallet."
        }
      ],
      safetyTips: [
        "Always pay from a bank account/UPI that matches your KYC name on AGCX.",
        "Never cancel an order after you have already sent money.",
        "If you face any issues, click 'Appeal' to get instant assistance from AGCX Support."
      ]
    },
    {
      id: "sell",
      title: "How to Sell Crypto with P2P",
      subtitle: "Learn how to sell your crypto and receive fiat on AGCX P2P marketplace.",
      image: "/images/p2p/p2p_trading_img2.png",
      tag: "Seller Guide",
      intro: "Learn how to convert your cryptocurrency into local fiat currency and receive funds directly into your verified bank account or payment wallet safely via AGCX Escrow.",
      steps: [
        {
          num: "01",
          heading: "Select 'SELL' & Filter Offers",
          detail: "Switch to the 'SELL' tab on the P2P Dashboard. Choose the crypto you wish to sell and filter by your preferred receiving payment method and minimum/maximum transaction limits."
        },
        {
          num: "02",
          heading: "Select an Offer & Enter Sell Amount",
          detail: "Choose a reputable buyer, click 'SELL', and specify the crypto quantity you want to cash out. Select the verified payment account where you wish to receive the fiat payment."
        },
        {
          num: "03",
          heading: "Wait for Buyer Payment",
          detail: "When you place the sell order, your crypto is securely locked in AGCX Escrow. Wait for the buyer to transfer the fiat amount within the specified payment window."
        },
        {
          num: "04",
          heading: "Verify Payment in Your Actual Bank Account",
          detail: "Open your own bank or payment app to confirm that the full amount has cleared. NEVER trust SMS notifications, payment screenshots, or third-party confirmations alone."
        },
        {
          num: "05",
          heading: "Release the Cryptocurrency",
          detail: "Once you have verified the money is in your account, click 'Payment Received & Release Crypto' and complete two-factor authentication (2FA) to finalize the transaction."
        }
      ],
      safetyTips: [
        "Never release crypto before confirming money in your own bank account.",
        "Do not accept payments from third-party accounts not matching the buyer's KYC name.",
        "If a buyer marks paid but you haven't received money, file an Appeal immediately."
      ]
    },
    {
      id: "overview",
      title: "Everything You Should Know About P2P Trading",
      subtitle: "Complete guide covering all aspects of P2P crypto trading on AGCX.",
      image: "/images/p2p/p2p_trading_img3.png",
      tag: "Comprehensive Guide",
      intro: "Master the core concepts of Peer-to-Peer (P2P) crypto trading, understand how escrow protection guarantees safety, and learn the essential rules to avoid scams.",
      steps: [
        {
          num: "01",
          heading: "What is P2P Trading?",
          detail: "Peer-to-Peer (P2P) is a decentralized marketplace allowing buyers and sellers to trade crypto directly using local fiat payment methods without paying any exchange trading fees."
        },
        {
          num: "02",
          heading: "How AGCX Escrow Keeps You Safe",
          detail: "During any P2P trade, AGCX acts as a trusted escrow intermediary. The seller's crypto is locked and cannot be moved until the seller confirms receiving fiat or an appeal is resolved."
        },
        {
          num: "03",
          heading: "0% Trading Fees",
          detail: "AGCX charges 0% fees on P2P trading. The price you see is the exact price you pay or receive, giving you optimal rates for your transactions."
        },
        {
          num: "04",
          heading: "Strict KYC & Verification Standards",
          detail: "All makers and takers undergo identity verification (KYC), email, and phone verification, providing an extra layer of transparency and trust across the platform."
        },
        {
          num: "05",
          heading: "24/7 Customer Support & Dispute Resolution",
          detail: "If an order cannot be resolved between both parties, either user can initiate an Appeal. AGCX 24/7 customer service will inspect bank proof and release assets fairly."
        }
      ],
      safetyTips: [
        "Never share sensitive passwords, OTPs, or contact information outside the AGCX chat.",
        "Be cautious of buyers/sellers offering off-platform transactions or unusual payment requests.",
        "Always communicate exclusively within the built-in AGCX order chat for official record."
      ]
    }
  ];

  const handlePayChange = (e) => {
    const val = e.target.value;
    setPayAmount(val);
    if (!isNaN(val) && val !== "") {
      setReceiveAmount((parseFloat(val) / 107.2).toFixed(2));
    } else {
      setReceiveAmount("");
    }
  };

  const handleReceiveChange = (e) => {
    const val = e.target.value;
    setReceiveAmount(val);
    if (!isNaN(val) && val !== "") {
      setPayAmount((parseFloat(val) * 107.2).toFixed(2));
    } else {
      setPayAmount("");
    }
  };

  const tokens = ["USDT", "BTC", "BUSD", "BNB", "ETH"];

  return (
    <P2pLayout title="Dashboard">
      <div className="p2p-dashboard-container">
        {/* ==================== 1. HERO SECTION ==================== */}
        <section className="p2p-hero-section">
          <div className="p2p-hero-content">
            <h1 className="p2p-hero-title">
              <span className="p2p-hero-title-dark">Trade Crypto</span>
              <br />
              <span className="p2p-hero-title-gold"> Your Way</span>
            </h1>
            <p className="p2p-hero-desc">
              Buy and sell crypto on Flexible with 500+ payment methods. Fast, secure and trusted by millions.
            </p>
            <div className="p2p-hero-actions">
              <button className="p2p-hero-btn p2p-hero-buy-btn">Buy Crypto</button>
              <button className="p2p-hero-btn p2p-hero-sell-btn">Sell Crypto</button>
            </div>
          </div>
          <div className="p2p-hero-image">
            <img src="/images/p2p/p2p_bnr_img.png" alt="Trade Crypto" />
          </div>
        </section>

        {/* ==================== 2. MAIN TRADING CARD & TABS ==================== */}
        <section className="p2p-trading-card">
          <div className="p2p-tabs-bar">
            <div className="p2p-token-tabs">
              {tokens.map(token => (
                <button
                  key={token}
                  className={`p2p-token-tab ${activeToken === token ? 'active' : ''}`}
                  onClick={() => setActiveToken(token)}
                >
                  {token}
                </button>
              ))}
              <div className="p2p-token-more dropdown">
                <button className="p2p-token-more-btn" type="button" data-bs-toggle="dropdown">
                  More <i className="ri-arrow-down-s-line"></i>
                </button>
                <ul className="dropdown-menu">
                  <li><a className="dropdown-item" href="#">USDC</a></li>
                  <li><a className="dropdown-item" href="#">XRP</a></li>
                </ul>
              </div>
            </div>
            <div className="p2p-buysell-switch">
              <button
                className={`p2p-switch-btn buybtn ${activeTab === 'buy' ? 'active-buy' : ''}`}
                onClick={() => setActiveTab('buy')}
              >
                BUY
              </button>
              <button
                className={`p2p-switch-btn sellbtn ${activeTab === 'sell' ? 'active-sell' : ''}`}
                onClick={() => setActiveTab('sell')}
              >
                SELL
              </button>
            </div>
          </div>

          {/* ==================== 3. FILTER CONTROLS BAR ==================== */}
          <div className="p2p-filter-bar">
            <div className="p2p-filter-group p2p-amount-group">
              <label>Amount</label>
              <div className="p2p-amount-input-wrap">
                <input
                  type="number"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <div className="p2p-currency-select">
                  <CustomSelect
                    value={currency}
                    onChange={setCurrency}
                    className="p2p-currency-dropdown"
                    options={[
                      { value: "INR", label: "₹ INR" },
                      { value: "USD", label: "$ USD" },
                      { value: "EUR", label: "€ EUR" }
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* <div className="p2p-filter-group">
              <label>Payment</label>
              <CustomSelect
                value={payment}
                onChange={setPayment}
                className="full-width-dropdown"
                options={[
                  { value: "All payments", label: "All payments" },
                  { value: "UPI", label: "UPI" },
                  { value: "Bank Transfer", label: "Bank Transfer" },
                  { value: "IMPS", label: "IMPS" }
                ]}
              />
            </div> */}

            <div className="p2p-filter-group">
              <label>Available Region(s)</label>
              <CustomSelect
                value={region}
                onChange={setRegion}
                className="full-width-dropdown"
                options={[
                  { value: "All Regions", label: "All Regions" },
                  { value: "India", label: "India" },
                  { value: "UAE", label: "UAE" }
                ]}
              />
            </div>

            <div className="p2p-filter-group p2p-filter-input-group">
              <label>Filter</label>
              <div className="p2p-filter-input-wrap">
                <img src="/images/p2p/fillter_icon.png" alt="Search" />
                {/* <input 
                  type="text" 
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                /> */}
              </div>
            </div>

            <div className="p2p-filter-refresh">
              <button className="p2p-refresh-btn">
                <i className="ri-refresh-line"></i> Refresh
              </button>
            </div>
          </div>

          {/* ==================== 4. ADVERTISERS TABLE & PAGINATION ==================== */}
          <div className="p2p-table-container">
            <table className="p2p-table">
              <thead>
                <tr>
                  <th>Advertisers</th>
                  <th>Price</th>
                  <th>Available / Limit</th>
                  <th>Payment</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {/* Row 1 */}
                <tr>
                  <td>
                    <div className="p2p-adv-cell">
                      <div className="p2p-adv-avatar dark-avatar">C</div>
                      <div className="p2p-adv-info">
                        <h4>CryptoPark</h4>
                        <span>98% | 1734 trades</span>
                      </div>
                    </div>
                  </td>
                  <td className="p2p-price-cell">₹ 81.25</td>
                  <td>
                    <div className="p2p-limit-cell">
                      <div className="p2p-limit-val">8,40,001.00 USDT</div>
                      <div className="p2p-limit-range">Limit 500 - 1,000</div>
                    </div>
                  </td>
                  <td>
                    <div className="p2p-payment-badges">
                      <span className="p2p-payment-tag">UPI</span>
                      <span className="p2p-payment-tag">NEFT</span>
                    </div>
                  </td>
                  <td>
                    <button
                      className={`p2p-action-btn ${activeTab === 'buy' ? 'btn-buy' : 'btn-sell'}`}
                      onClick={() => setIsModalOpen(true)}
                    >
                      {activeTab === 'buy' ? 'Buy' : 'Sell'}
                    </button>
                  </td>
                </tr>
                {/* Row 2 */}
                <tr>
                  <td>
                    <div className="p2p-adv-cell">
                      <div className="p2p-adv-avatar dark-avatar">T</div>
                      <div className="p2p-adv-info">
                        <h4>TradeSúper</h4>
                        <span>97% | 682 trades</span>
                      </div>
                    </div>
                  </td>
                  <td className="p2p-price-cell">₹ 81.28</td>
                  <td>
                    <div className="p2p-limit-cell">
                      <div className="p2p-limit-val">1,80,45.500 USDT</div>
                      <div className="p2p-limit-range">Limit 500 - 80,000</div>
                    </div>
                  </td>
                  <td>
                    <div className="p2p-payment-badges">
                      <span className="p2p-payment-tag">Amazon Pay</span>
                      <span className="p2p-payment-tag">NEFT</span>
                    </div>
                  </td>
                  <td>
                    <button
                      className={`p2p-action-btn ${activeTab === 'buy' ? 'btn-buy' : 'btn-sell'}`}
                      onClick={() => setIsModalOpen(true)}
                    >
                      {activeTab === 'buy' ? 'Buy' : 'Sell'}
                    </button>
                  </td>
                </tr>
                {/* Row 3 */}
                <tr>
                  <td>
                    <div className="p2p-adv-cell">
                      <div className="p2p-adv-avatar dark-avatar">H</div>
                      <div className="p2p-adv-info">
                        <h4>Hatfield</h4>
                        <span>96% | 453 trades</span>
                      </div>
                    </div>
                  </td>
                  <td className="p2p-price-cell">₹ 82.20</td>
                  <td>
                    <div className="p2p-limit-cell">
                      <div className="p2p-limit-val">1,96,452 USDT</div>
                      <div className="p2p-limit-range">Limit 500 - 25,000</div>
                    </div>
                  </td>
                  <td>
                    <div className="p2p-payment-badges">
                      <span className="p2p-payment-tag">Paytm</span>
                    </div>
                  </td>
                  <td>
                    <button
                      className={`p2p-action-btn ${activeTab === 'buy' ? 'btn-buy' : 'btn-sell'}`}
                      onClick={() => setIsModalOpen(true)}
                    >
                      {activeTab === 'buy' ? 'Buy' : 'Sell'}
                    </button>
                  </td>
                </tr>
                {/* Row 4 */}
                <tr>
                  <td>
                    <div className="p2p-adv-cell">
                      <div className="p2p-adv-avatar dark-avatar">B</div>
                      <div className="p2p-adv-info">
                        <h4>BitTrader</h4>
                        <span>91% | 212 trades</span>
                      </div>
                    </div>
                  </td>
                  <td className="p2p-price-cell">₹ 82.35</td>
                  <td>
                    <div className="p2p-limit-cell">
                      <div className="p2p-limit-val">2,00,400 USDT</div>
                      <div className="p2p-limit-range">Limit 500 - 1,000</div>
                    </div>
                  </td>
                  <td>
                    <div className="p2p-payment-badges">
                      <span className="p2p-payment-tag">UPI</span>
                    </div>
                  </td>
                  <td>
                    <button
                      className={`p2p-action-btn ${activeTab === 'buy' ? 'btn-buy' : 'btn-sell'}`}
                      onClick={() => setIsModalOpen(true)}
                    >
                      {activeTab === 'buy' ? 'Buy' : 'Sell'}
                    </button>
                  </td>
                </tr>
                {/* Row 5 */}
                <tr>
                  <td>
                    <div className="p2p-adv-cell">
                      <div className="p2p-adv-avatar dark-avatar">O</div>
                      <div className="p2p-adv-info">
                        <h4>OnlineTrade</h4>
                        <span>94% | 287 trades</span>
                      </div>
                    </div>
                  </td>
                  <td className="p2p-price-cell">₹ 83.00</td>
                  <td>
                    <div className="p2p-limit-cell">
                      <div className="p2p-limit-val">1,90,000 USDT</div>
                      <div className="p2p-limit-range">Limit 500 - 2,500</div>
                    </div>
                  </td>
                  <td>
                    <div className="p2p-payment-badges">
                      <span className="p2p-payment-tag">Bank Transfer</span>
                    </div>
                  </td>
                  <td>
                    <button
                      className={`p2p-action-btn ${activeTab === 'buy' ? 'btn-buy' : 'btn-sell'}`}
                      onClick={() => setIsModalOpen(true)}
                    >
                      {activeTab === 'buy' ? 'Buy' : 'Sell'}
                    </button>
                  </td>
                </tr>
                {/* Row 6 */}
                <tr>
                  <td>
                    <div className="p2p-adv-cell">
                      <div className="p2p-adv-avatar dark-avatar">D</div>
                      <div className="p2p-adv-info">
                        <h4>DraftForex</h4>
                        <span>93% | 198 trades</span>
                      </div>
                    </div>
                  </td>
                  <td className="p2p-price-cell">₹ 83.45</td>
                  <td>
                    <div className="p2p-limit-cell">
                      <div className="p2p-limit-val">2,08,000 USDT</div>
                      <div className="p2p-limit-range">Limit 500 - 2,000</div>
                    </div>
                  </td>
                  <td>
                    <div className="p2p-payment-badges">
                      <span className="p2p-payment-tag">Paytm</span>
                    </div>
                  </td>
                  <td>
                    <button
                      className={`p2p-action-btn ${activeTab === 'buy' ? 'btn-buy' : 'btn-sell'}`}
                      onClick={() => setIsModalOpen(true)}
                    >
                      {activeTab === 'buy' ? 'Buy' : 'Sell'}
                    </button>
                  </td>
                </tr>
                {/* Row 7 */}
                <tr>
                  <td>
                    <div className="p2p-adv-cell">
                      <div className="p2p-adv-avatar dark-avatar">G</div>
                      <div className="p2p-adv-info">
                        <h4>GemderBiter</h4>
                        <span>90% | 148 trades</span>
                      </div>
                    </div>
                  </td>
                  <td className="p2p-price-cell">₹ 83.45</td>
                  <td>
                    <div className="p2p-limit-cell">
                      <div className="p2p-limit-val">2,08,000 USDT</div>
                      <div className="p2p-limit-range">Limit 500 - 1,000</div>
                    </div>
                  </td>
                  <td>
                    <div className="p2p-payment-badges">
                      <span className="p2p-payment-tag">UPI</span>
                    </div>
                  </td>
                  <td>
                    <button
                      className={`p2p-action-btn ${activeTab === 'buy' ? 'btn-buy' : 'btn-sell'}`}
                      onClick={() => setIsModalOpen(true)}
                    >
                      {activeTab === 'buy' ? 'Buy' : 'Sell'}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="p2p-pagination-center">
              <button className="p2p-page-arrow">&lt;</button>
              <button className="p2p-page-num active">1</button>
              <button className="p2p-page-num">2</button>
              <button className="p2p-page-num">3</button>
              <button className="p2p-page-num">4</button>
              <span className="p2p-page-dots">...</span>
              <button className="p2p-page-num">10</button>
              <button className="p2p-page-arrow">&gt;</button>
            </div>
          </div>
        </section>

        {/* ==================== 5. TRADING TUTORIAL SECTION ==================== */}
        <section className="p2p-tutorial-section">
          <div className="p2p-section-header">
            <h2>P2P Trading Tutorial</h2>
            <p>Learn how to trade safely on AGCX P2P</p>
          </div>

          <div className="p2p-tutorial-grid">
            {tutorialCards.map((tut) => (
              <div key={tut.id} className="p2p-tutorial-card">
                <div className="p2p-tut-img-wrap">
                  <img src={tut.image} alt={tut.title} />
                </div>
                <div className="p2p-tut-content">
                  <h4>{tut.title}</h4>
                  <p>{tut.subtitle}</p>
                  <button
                    type="button"
                    className="p2p-tut-link"
                    onClick={() => setSelectedTutorial(tut)}
                  >
                    Read More <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ==================== 6. FAQ ACCORDION SECTION ==================== */}
        <section className="p2p-faq-section">
          <div className="p2p-section-header">
            <h2>P2P Trading FAQ</h2>
            <p>Frequently asked questions about P2P trading</p>
          </div>

          <div className="p2p-accordion" id="p2pFAQAccordion">
            <div className="p2p-accordion-item">
              <h2 className="p2p-accordion-header" id="faq-headingOne">
                <button className="p2p-accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq-collapseOne" aria-expanded="false" aria-controls="faq-collapseOne">
                  What is P2P Trading on AGCX?
                  <span className="p2p-acc-icon">+</span>
                </button>
              </h2>
              <div id="faq-collapseOne" className="accordion-collapse collapse" aria-labelledby="faq-headingOne" data-bs-parent="#p2pFAQAccordion">
                <div className="p2p-accordion-body">
                  P2P (Peer-to-Peer) trading on AGCX lets you buy and sell crypto directly with other users using your preferred fiat payment method. AGCX escrow holds the crypto until payment is confirmed.
                </div>
              </div>
            </div>

            <div className="p2p-accordion-item">
              <h2 className="p2p-accordion-header" id="faq-headingTwo">
                <button className="p2p-accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq-collapseTwo" aria-expanded="false" aria-controls="faq-collapseTwo">
                  How does P2P Trading work?
                  <span className="p2p-acc-icon">+</span>
                </button>
              </h2>
              <div id="faq-collapseTwo" className="accordion-collapse collapse" aria-labelledby="faq-headingTwo" data-bs-parent="#p2pFAQAccordion">
                <div className="p2p-accordion-body">
                  Buyers place an order on AGCX P2P, send payment to the seller via the chosen method, and once confirmed, AGCX escrow releases the cryptocurrency to the buyer's AGCX wallet.
                </div>
              </div>
            </div>

            <div className="p2p-accordion-item">
              <h2 className="p2p-accordion-header" id="faq-headingThree">
                <button className="p2p-accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq-collapseThree" aria-expanded="false" aria-controls="faq-collapseThree">
                  Is P2P Trading on AGCX safe?
                  <span className="p2p-acc-icon">+</span>
                </button>
              </h2>
              <div id="faq-collapseThree" className="accordion-collapse collapse" aria-labelledby="faq-headingThree" data-bs-parent="#p2pFAQAccordion">
                <div className="p2p-accordion-body">
                  Yes, AGCX uses an escrow mechanism to lock the seller's crypto until payment is confirmed, ensuring safety for both parties.
                </div>
              </div>
            </div>

            <div className="p2p-accordion-item">
              <h2 className="p2p-accordion-header" id="faq-headingFour">
                <button className="p2p-accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq-collapseFour" aria-expanded="false" aria-controls="faq-collapseFour">
                  Does AGCX charge any P2P trading fees?
                  <span className="p2p-acc-icon">+</span>
                </button>
              </h2>
              <div id="faq-collapseFour" className="accordion-collapse collapse" aria-labelledby="faq-headingFour" data-bs-parent="#p2pFAQAccordion">
                <div className="p2p-accordion-body">
                  No! AGCX offers 0% trading fees on P2P buy and sell orders.
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* ==================== 6. BUY/SELL MODAL ==================== */}
      {isModalOpen && typeof document !== "undefined" && createPortal(
        <div className="p2p-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="p2p-modal-content" onClick={e => e.stopPropagation()}>
            <div className="p2p-modal-body">

              {/* --- Left Side: Seller Info --- */}
              <div className="p2p-modal-left">
                <div className="p2p-modal-seller-header">
                  <div className="p2p-modal-avatar">B</div>
                  <div className="p2p-modal-seller-info">
                    <h3>BHOLE SHANKAR SINGH</h3>
                    <div className="p2p-modal-badges">
                      <span className="p2p-badge-green"><i className="ri-checkbox-circle-fill"></i> KYC Verified</span>
                      <span className="p2p-badge-green"><i className="ri-checkbox-circle-fill"></i> Email Verified</span>
                      <span className="p2p-badge-green"><i className="ri-checkbox-circle-fill"></i> Phone Verified</span>
                    </div>
                    <p className="p2p-modal-last-online">Last Online: 19 minute(s) ago</p>
                  </div>
                </div>

                <div className="p2p-modal-stats-row">
                  <div className="p2p-modal-stat-box">
                    <div className="d-flex twop2p"><span className="p2p-stat-icon"><i className="ri-line-chart-line"></i></span>
                      <div className="p2p-stat-val">
                        <strong>4,108</strong>
                      </div>
                    </div>

                    <p>Total Trades</p>
                  </div>
                  <div className="p2p-modal-stat-box">
                    <div className="d-flex twop2p">
                      <span className="p2p-stat-icon"><i className="ri-refresh-line"></i></span>
                      <div className="p2p-stat-val">
                        <strong>86.83%</strong>
                      </div>
                    </div>

                    <p>Completion Rate</p>
                  </div>
                </div>

                <div className="p2p-modal-about">
                  <h4>About the Seller</h4>
                  <p>Highly trusted seller on AGCX P2P</p>
                  <p>Fast responses & reliable</p>
                  <p>Trade with confidence</p>
                </div>

                <div className="p2p-modal-remarks">
                  <h4>Remarks</h4>
                  <div className="p2p-remarks-box">
                    hi 6299855626 only for communication not for payment
                  </div>
                </div>

                <div className="p2p-modal-payments">
                  <h4>Supported Payment Methods</h4>
                  <div className="p2p-payment-icons">
                    <span className="p2p-pay-icon"><i className="ri-bank-card-line"></i> UPI</span>
                    <span className="p2p-pay-icon"><i className="ri-smartphone-line"></i> PhonePe</span>
                    <span className="p2p-pay-icon"><i className="ri-wallet-3-line"></i> Paytm</span>
                    <span className="p2p-pay-icon"><i className="ri-bank-line"></i> Bank</span>
                    <div className="p2p-payment-more">...</div>
                  </div>
                </div>
              </div>

              {/* --- Right Side: Transaction Details --- */}
              <div className="p2p-modal-right">
                <button className="p2p-modal-close" onClick={() => setIsModalOpen(false)}>
                  <i className="ri-close-line"></i>
                </button>

                <div className="p2p-modal-action-header">
                  <div className="p2p-action-title">
                    <div className="p2p-token-circle"><i className="ri-money-dollar-circle-fill"></i></div>
                    <h2>Buy USDT</h2>
                  </div>
                </div>

                <div className="p2p-price-secure-row">
                  <div className="p2p-price-rate">Price 1 USDT &approx; ₹107.2 INR</div>
                  <div className="p2p-secure-badge">
                    <i className="ri-shield-check-fill"></i> Secure <span>| Escrow Protected</span>
                  </div>
                </div>

                <div className="p2p-input-group">
                  <label>You pay</label>
                  <div className="p2p-amount-box">
                    <span className="p2p-currency-symbol">₹</span>
                    <input type="text" placeholder="0.00" value={payAmount} onChange={handlePayChange} />
                    <div className="p2p-currency-dropdown-sm">
                      INR <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </div>
                  <div className="p2p-limit-hint">Order limit: ₹10,000 - ₹50,001</div>
                </div>

                <div className="p2p-input-group">
                  <label>You receive</label>
                  <div className="p2p-amount-box">
                    <div className="p2p-token-circle-sm"><i className="ri-money-dollar-circle-fill"></i></div>
                    <input type="text" placeholder="0.00" value={receiveAmount} onChange={handleReceiveChange} />
                    <div className="p2p-currency-dropdown-sm">
                      USDT <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </div>
                  <div className="p2p-limit-hint">Available balance: <span>0 USDT</span></div>
                </div>

                <div className="p2p-input-group" style={{ position: 'relative' }}>
                  <label>Payment Method</label>
                  <div className="p2p-payment-select-box" onClick={() => setIsPaymentDropdownOpen(!isPaymentDropdownOpen)}>
                    <div className="p2p-pay-sel-left">
                      <i className={modalPaymentMethod === 'UPI' ? 'ri-smartphone-line' : (modalPaymentMethod === 'Bank Transfer' ? 'ri-bank-line' : (modalPaymentMethod === 'Paytm' ? 'ri-wallet-3-line' : 'ri-bank-card-line'))}></i> {modalPaymentMethod}
                    </div>
                    <i className="ri-arrow-down-s-line p2p-select-arrow" style={{ transform: isPaymentDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'all 0.3s' }}></i>
                  </div>
                  {isPaymentDropdownOpen && (
                    <div className="p2p-payment-dropdown-list">
                      <div className="p2p-payment-dropdown-item" onClick={() => { setModalPaymentMethod('Other'); setIsPaymentDropdownOpen(false); }}>
                        <i className="ri-bank-card-line"></i> Other
                      </div>
                      <div className="p2p-payment-dropdown-item" onClick={() => { setModalPaymentMethod('UPI'); setIsPaymentDropdownOpen(false); }}>
                        <i className="ri-smartphone-line"></i> UPI
                      </div>
                      <div className="p2p-payment-dropdown-item" onClick={() => { setModalPaymentMethod('Bank Transfer'); setIsPaymentDropdownOpen(false); }}>
                        <i className="ri-bank-line"></i> Bank Transfer
                      </div>
                      <div className="p2p-payment-dropdown-item" onClick={() => { setModalPaymentMethod('Paytm'); setIsPaymentDropdownOpen(false); }}>
                        <i className="ri-wallet-3-line"></i> Paytm
                      </div>
                    </div>
                  )}
                </div>

                <div className="p2p-escrow-info">
                  <i className="ri-shield-check-fill"></i>
                  <div>
                    <strong>AGCX holds the seller's crypto in escrow</strong>
                    <p>You will receive the USDT only after payment is confirmed.</p>
                  </div>
                </div>

                <div className="p2p-terms-check">
                  <label>
                    <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} />
                    <span>By placing an order, you agree to the <a href="#">P2P Trading Terms of Use.</a></span>
                  </label>
                </div>

                <button className="p2p-submit-buy-btn" onClick={() => { setIsRequirementModalOpen(true); setIsModalOpen(false); }}>
                  Buy USDT
                </button>
              </div>

            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ==================== 7. TRADING REQUIREMENT MODAL ==================== */}
      {isRequirementModalOpen && typeof document !== "undefined" && createPortal(
        <div className="p2p-modal-overlay" style={{ zIndex: 1000000000 }} onClick={() => setIsRequirementModalOpen(false)}>
          <div className="p2p-req-modal-content" onClick={e => e.stopPropagation()}>
            <div className="p2p-req-modal-header">
              <h2>Trading Requirement</h2>
              <button className="p2p-modal-close-req" onClick={() => setIsRequirementModalOpen(false)}>
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p2p-req-modal-body">
              <div className="p2p-req-item">
                <span className="p2p-req-text">Complete KYC Verification</span>
                <button className="p2p-req-verify-btn">Verify</button>
              </div>
              <div className="p2p-req-item">
                <span className="p2p-req-text">Enable SMS authentication</span>
                <span className="p2p-req-done">Done <i className="ri-checkbox-circle-fill"></i></span>
              </div>
            </div>
            <div className="p2p-req-modal-footer">
              <button className="p2p-req-cancel-btn" onClick={() => setIsRequirementModalOpen(false)}>Cancel</button>
              <button
                className="p2p-req-get-verified-btn"
                onClick={() => {
                  setIsRequirementModalOpen(false);
                  navigate('/p2p-trade-order');
                }}
              >
                Get Verified
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ==================== 8. P2P TUTORIAL MODAL ==================== */}
      {selectedTutorial && typeof document !== "undefined" && createPortal(
        <div className="p2p-modal-overlay" style={{ zIndex: 1000000000 }} onClick={() => setSelectedTutorial(null)}>
          <div className="p2p-req-modal-content p2p-tutorial-modal-content" onClick={e => e.stopPropagation()}>
            <div className="p2p-req-modal-header">
              <div className="p2p-tut-modal-header-left">
                <span className="p2p-tut-modal-badge">{selectedTutorial.tag}</span>
                <h2>{selectedTutorial.title}</h2>
              </div>
              <button className="p2p-modal-close-req" onClick={() => setSelectedTutorial(null)} aria-label="Close">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="p2p-req-modal-body p2p-tut-modal-body">
              <p className="p2p-tut-modal-intro">{selectedTutorial.intro}</p>

              <div className="p2p-tut-modal-steps">
                {selectedTutorial.steps.map((st, i) => (
                  <div key={i} className="p2p-tut-step-card">
                    <div className="p2p-tut-step-num">{st.num}</div>
                    <div className="p2p-tut-step-content">
                      <h4>{st.heading}</h4>
                      <p>{st.detail}</p>
                    </div>
                  </div>
                ))}
              </div>

              {selectedTutorial.safetyTips && (
                <div className="p2p-tut-safety-box">
                  <div className="p2p-tut-safety-title">
                    <i className="ri-shield-check-line"></i>
                    <span>Important Security Rules</span>
                  </div>
                  <ul>
                    {selectedTutorial.safetyTips.map((tip, idx) => (
                      <li key={idx}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="p2p-req-modal-footer">
              <button className="p2p-req-cancel-btn" onClick={() => setSelectedTutorial(null)}>
                Close
              </button>
              <button className="p2p-req-get-verified-btn p2p-tut-gotit-btn" onClick={() => setSelectedTutorial(null)}>
                Got It
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </P2pLayout>
  );
};

export default P2pDashboard;
